import * as React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { styled, useTheme, createTheme, ThemeProvider } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import {
    Box,
    CssBaseline,
    Toolbar,
    IconButton,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Drawer,
    Divider,
    Backdrop,
    CircularProgress,
} from '@mui/material';
import { BrowserRouter as Router } from 'react-router-dom';
import AppRoutes from './AppRoutes';
import MoreIcon from '@mui/icons-material/MoreVert';
import Settings from '@mui/icons-material/Settings';
import VpnKey from '@mui/icons-material/VpnKey';
import Logout from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Person } from '@microsoft/mgt-react';
import DefaultListItems from './DefaultListItems';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { importCommonInfo } from '../../redux/actions/commonInfoActions';
import { getEmployeeData } from '../../redux/actions/employeeActions';
import { NavLink } from 'react-router-dom';
import { setCompanyDetails } from '../../redux/actions/companyDetailsActions';
import { isEmployeeSystemAdmin } from '../../redux/selectors/employeeSelector';

const drawerWidth: number = 210;
const mdTheme = createTheme({
    components: {
        // Veći ciljevi za prst: meniji i stavke lista su najmanje 48px visoke na telefonu.
        MuiMenuItem: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down('sm')]: { minHeight: 48, paddingTop: 12, paddingBottom: 12 },
                }),
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    [theme.breakpoints.down('sm')]: { minHeight: 56 },
                }),
            },
        },
        MuiIconButton: {
            styleOverrides: {
                sizeSmall: ({ theme }) => ({
                    [theme.breakpoints.down('sm')]: { padding: 8 },
                }),
            },
        },
    },
});

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0),
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: '-100%',
    },
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(0),
        flexGrow: 1,
        transition: theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
        }),
        marginLeft: `-${drawerWidth}px`,
        ...(open && {
            transition: theme.transitions.create('margin', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
            marginLeft: 0,
        }),
    },
}));

interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}

const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        [theme.breakpoints.down('sm')]: {
            width: 0,
        },
        [theme.breakpoints.up('sm')]: {
            width: `calc(100% - ${drawerWidth}px)`,
            marginLeft: `${drawerWidth}px`,
            transition: theme.transitions.create(['margin', 'width'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.enteringScreen,
            }),
        },
    }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: 'flex-end',
}));

function DashboardContent() {
    const theme = useTheme();

    const apiKey = useAppSelector((state) => state.commonInfo.googleMapApiKey);

    const employeeData = useAppSelector((state) => state.loggedEmployee.basicEmployee);
    const roles = useAppSelector((state) => state.loggedEmployee.readableRoles);
    const roless = useAppSelector((state) => state.loggedEmployee.roles);

    const isSystemAdmin = useAppSelector(isEmployeeSystemAdmin);

    const { instance } = useMsal();

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (!apiKey) {
            dispatch(importCommonInfo());
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (!!dispatch) {
            dispatch(setCompanyDetails());
        }
    }, [dispatch]);

    const isAuthenticated = useIsAuthenticated();

    React.useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        dispatch(getEmployeeData());
    }, [isAuthenticated, instance]);

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const [open, setOpen] = React.useState(false);

    const handleDrawerOpen = () => {
        setOpen(true);
    };

    const handleDrawerClose = () => {
        setOpen(false);
    };

    const handleCloseDrawer = () => {
        const widthOfTheWindow = window.innerWidth;

        if (widthOfTheWindow < 600) {
            setOpen(false);
        }
    };

    const logOutUser = (): void => {
        instance.logoutRedirect().catch((e) => {
            console.error(e);
        });
    };

    return !Boolean(employeeData) ? (
        <React.Fragment>
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={!Boolean(employeeData)}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </React.Fragment>
    ) : (
        <Router>
            <ThemeProvider theme={mdTheme}>
                <Box sx={{ display: 'flex' }}>
                    <CssBaseline />
                    <AppBar
                        position="fixed"
                        open={open}
                        sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
                        color="inherit"
                        elevation={1}
                    >
                        <Toolbar>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                onClick={handleDrawerOpen}
                                edge="start"
                                sx={{ mr: 2, ...(open && { display: 'none' }) }}
                            >
                                <MenuIcon />
                            </IconButton>
                            <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}></Typography>
                            <Person userId={instance.getAllAccounts()[0].localAccountId}></Person>
                            <IconButton
                                size="small"
                                aria-label="display more actions"
                                edge="end"
                                color="inherit"
                                onClick={handleClick}
                            >
                                <MoreIcon />
                            </IconButton>
                            <Menu
                                anchorEl={anchorEl}
                                open={openMenu}
                                onClose={handleClose}
                                onClick={handleClose}
                                PaperProps={{
                                    elevation: 0,
                                    sx: {
                                        overflow: 'visible',
                                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                                        mt: 1.5,
                                        '& .MuiAvatar-root': {
                                            width: 32,
                                            height: 32,
                                            ml: -0.5,
                                            mr: 1,
                                        },
                                        '&:before': {
                                            content: '""',
                                            display: 'block',
                                            position: 'absolute',
                                            top: 0,
                                            right: 14,
                                            width: 10,
                                            height: 10,
                                            bgcolor: 'background.paper',
                                            transform: 'translateY(-50%) rotate(45deg)',
                                            zIndex: 0,
                                        },
                                    },
                                }}
                                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                            >
                                <MenuItem disabled={!isSystemAdmin}>
                                    <NavLink to={'settings'} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <ListItemIcon>
                                            <Settings fontSize="small" />
                                        </ListItemIcon>
                                        Podešavanja
                                    </NavLink>
                                </MenuItem>
                                <MenuItem disabled={!isSystemAdmin}>
                                    <NavLink to={'apiTokens'} style={{ textDecoration: 'none', color: 'inherit' }}>
                                        <ListItemIcon>
                                            <VpnKey fontSize="small" />
                                        </ListItemIcon>
                                        API tokeni
                                    </NavLink>
                                </MenuItem>
                                <MenuItem onClick={logOutUser}>
                                    <ListItemIcon>
                                        <Logout fontSize="small" />
                                    </ListItemIcon>
                                    Odjavi se
                                </MenuItem>
                            </Menu>
                        </Toolbar>
                    </AppBar>
                    <Drawer
                        sx={{
                            width: { xs: '100%', sm: drawerWidth },
                            flexShrink: 0,
                            [`& .MuiDrawer-paper`]: {
                                width: { xs: '100%', sm: drawerWidth },
                                boxSizing: 'border-box',
                            },
                        }}
                        variant="persistent"
                        anchor="left"
                        open={open}
                    >
                        <DrawerHeader sx={{ justifyContent: 'space-between' }}>
                            <Box
                                component="img"
                                marginLeft={1.2}
                                sx={{
                                    height: 40,
                                    width: 40,
                                    maxHeight: { xs: 233, md: 167 },
                                    maxWidth: { xs: 350, md: 250 },
                                }}
                                alt="The house from the offer."
                                src="./box.png"
                            />
                            <Typography variant="subtitle1" noWrap component="div" sx={{ ml: 2, mr: 1 }}>
                                Warehouse
                            </Typography>
                            <IconButton onClick={handleDrawerClose} sx={{ mr: '-5px' }}>
                                {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                            </IconButton>
                        </DrawerHeader>
                        <Divider />
                        <Box sx={{ overflow: 'auto' }}>
                            <DefaultListItems closeDrawer={handleCloseDrawer} />
                        </Box>
                    </Drawer>
                    <Main
                        open={open}
                        sx={{
                            backgroundColor: (theme) =>
                                theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
                            flexGrow: 1,
                            height: '100vh',
                            overflow: 'auto',
                        }}
                    >
                        <DrawerHeader />

                        {/**
                         * -------------------------------------------------------------------
                         * App routes
                         *
                         * */}

                        <AppRoutes />

                        {/**
                         * -------------------------------------------------------------------
                         * */}
                    </Main>
                </Box>
            </ThemeProvider>
        </Router>
    );
}

export default function Navigation() {
    return <DashboardContent />;
}
